import React from "react";
import PointsTiles, {
  type PointsTilesImage,
  type PointsTilesLayout,
  type PointsTilesTile,
  type PointsTilesVariant,
} from "../../feature-tiles/points-tiles/PointsTiles";
import {
  serviceBlockTokens,
  type ServiceBlockSpacingToken,
} from "./ServiceBlock.tokens";

export type ServiceBlockImage = PointsTilesImage;

export type ServiceBlockCategory = {
  id?: string | number;
  title: string;
  services: string[];
  imageUrl?: string;
  image?: ServiceBlockImage;
  imageAlt?: string;
  imagePosition?: string;
  icon?: React.ReactNode;
  iconSrc?: string;
  iconAlt?: string;
  accentColor?: string;
  accentSoftColor?: string;
  accentDarkColor?: string;
};

export type ServiceBlockProps = {
  data: ServiceBlockCategory[];
  eyebrow?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
  pointsTilesClassName?: string;
  headerClassName?: string;
  gridClassName?: string;
  cardClassName?: string;
  pointsClasses?: string;
  pointTitleClasses?: string;
  layout?: PointsTilesLayout;
  pointsVariant?: PointsTilesVariant;
  backgroundColor?: string;
  maxWidth?: number | string;
  imageHeight?: number | string;
  imageHeightMobile?: number | string;
  bottomSpacing?: ServiceBlockSpacingToken | number | string;
  ariaLabel?: string;
};

type ServiceBlockStyle = React.CSSProperties &
  Record<`--${string}`, string | number | undefined>;

const t = serviceBlockTokens;

const serviceBlockCss = `
.service-block {
  --service-block-bg: ${t.colors.background};
  --service-block-padding-bottom: ${t.spacing.default}px;
  width: 100%;
  padding-bottom: var(--service-block-padding-bottom);
  background: var(--service-block-bg);
  box-sizing: border-box;
}
.service-block,
.service-block * {
  box-sizing: border-box;
}
`;

const joinClassNames = (...classNames: Array<string | false | null | undefined>) =>
  classNames.filter(Boolean).join(" ");

const toCssSize = (value?: number | string) => {
  if (typeof value === "number") {
    return `${value}px`;
  }

  return value;
};

const getSpacing = (value: ServiceBlockProps["bottomSpacing"]) => {
  if (value === undefined) {
    return t.spacing.default;
  }

  if (typeof value === "string" && value in t.spacing) {
    return t.spacing[value as ServiceBlockSpacingToken];
  }

  return value;
};

const toPointsTile = (category: ServiceBlockCategory, index: number): PointsTilesTile => ({
  id: category.id ?? `${category.title}-${index}`,
  title: category.title,
  points: category.services,
  imageUrl: category.imageUrl,
  image: category.image,
  imageAlt: category.imageAlt,
  imagePosition: category.imagePosition,
  icon: category.icon,
  iconSrc: category.iconSrc,
  iconAlt: category.iconAlt,
  accentColor: category.accentColor,
  accentSoftColor: category.accentSoftColor,
  accentDarkColor: category.accentDarkColor,
});

const ServiceBlock = ({
  data,
  eyebrow,
  title,
  description,
  className,
  pointsTilesClassName,
  headerClassName,
  gridClassName,
  cardClassName,
  pointsClasses = t.classes.point,
  pointTitleClasses = t.classes.pointTitle,
  layout = t.layout,
  pointsVariant = t.variant,
  backgroundColor,
  maxWidth,
  imageHeight,
  imageHeightMobile,
  bottomSpacing,
  ariaLabel,
}: ServiceBlockProps) => {
  const rootStyle: ServiceBlockStyle = {
    "--service-block-bg": backgroundColor || t.colors.background,
    "--service-block-padding-bottom": toCssSize(getSpacing(bottomSpacing)),
  };

  return (
    <div className={joinClassNames("service-block", className)} style={rootStyle}>
      <style>{serviceBlockCss}</style>

      <PointsTiles
        pointsData={data.map(toPointsTile)}
        pointsClasses={pointsClasses}
        pointTitleClasses={pointTitleClasses}
        eyebrow={eyebrow}
        title={title}
        description={description}
        className={pointsTilesClassName}
        headerClassName={headerClassName}
        gridClassName={gridClassName}
        cardClassName={cardClassName}
        layout={layout}
        variant={pointsVariant}
        backgroundColor={backgroundColor}
        maxWidth={maxWidth}
        imageHeight={imageHeight}
        imageHeightMobile={imageHeightMobile}
        ariaLabel={ariaLabel}
      />
    </div>
  );
};

export { serviceBlockTokens } from "./ServiceBlock.tokens";
export type { ServiceBlockSpacingToken } from "./ServiceBlock.tokens";

export default ServiceBlock;
